import { z } from 'zod';
import type { TFunction } from 'i18next';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import {
  LanguageSchema,
  NAME_MAX,
  NAME_MIN,
  PASSWORD_DIGIT_REGEX,
  PASSWORD_LETTER_REGEX,
  PASSWORD_MIN,
  RoleSchema,
  USERNAME_MAX,
  USERNAME_MIN,
  USERNAME_REGEX,
} from '@forest/contracts';
import type { ApiErrorDetail, ApiErrorInfo } from '@/lib/api-client';

// Localized form schemas built from the contract rule constants. Keys use
// an explicit 'common:' prefix so they resolve from any caller namespace.
// A drift-guard test asserts parity with the contract schemas.

export function makeNameSchema(t: TFunction) {
  return z
    .string()
    .min(1, t('common:validation.required'))
    .min(NAME_MIN, t('common:validation.name.tooShort'))
    .max(NAME_MAX, t('common:validation.name.tooLong'));
}

export function makeUsernameSchema(t: TFunction) {
  return z
    .string()
    .min(1, t('common:validation.required'))
    .min(USERNAME_MIN, t('common:validation.username.tooShort'))
    .max(USERNAME_MAX, t('common:validation.username.tooLong'))
    .regex(USERNAME_REGEX, t('common:validation.username.charset'));
}

export function makePasswordSchema(t: TFunction) {
  return z
    .string()
    .min(1, t('common:validation.required'))
    .min(PASSWORD_MIN, t('common:validation.password.tooShort'))
    .regex(PASSWORD_LETTER_REGEX, t('common:validation.password.needsLetter'))
    .regex(PASSWORD_DIGIT_REGEX, t('common:validation.password.needsDigit'));
}

export function makeRegisterFormSchema(t: TFunction) {
  return z
    .object({
      name: makeNameSchema(t),
      username: makeUsernameSchema(t),
      password: makePasswordSchema(t),
      confirmPassword: z.string(),
      preferredLanguage: LanguageSchema.default('ro'),
    })
    .refine((d) => d.password === d.confirmPassword, {
      message: t('auth:register.passwordMismatch'),
      path: ['confirmPassword'],
    });
}
export type RegisterFormValues = z.infer<ReturnType<typeof makeRegisterFormSchema>>;

export function makeCreateAdminUserFormSchema(t: TFunction) {
  return z.object({
    username: makeUsernameSchema(t),
    password: makePasswordSchema(t),
    name: makeNameSchema(t),
    role: RoleSchema,
    preferredLanguage: LanguageSchema,
    // '' is the form-level "no manager" sentinel; onSubmit maps it to null.
    managerId: z.union([z.literal(''), z.string().uuid()]),
  });
}
export type CreateAdminUserFormValues = z.infer<ReturnType<typeof makeCreateAdminUserFormSchema>>;

/** Returns the localized message for a known path+code, else null. */
export function localizeServerDetail(
  detail: ApiErrorDetail,
  t: TFunction,
  getValue?: (field: string) => unknown,
): string | null {
  const root = detail.path.split('.')[0];
  switch (`${root}/${detail.code}`) {
    case 'name/too_small':
      return t('common:validation.name.tooShort');
    case 'name/too_big':
      return t('common:validation.name.tooLong');
    case 'username/too_small':
      return t('common:validation.username.tooShort');
    case 'username/too_big':
      return t('common:validation.username.tooLong');
    case 'username/invalid_string':
      return t('common:validation.username.charset');
    case 'password/too_small':
      return t('common:validation.password.tooShort');
    case 'password/invalid_string': {
      // Both password regexes emit invalid_string; disambiguate by testing
      // the current form value against the contract regexes.
      if (!getValue) return null;
      const value = getValue('password');
      if (typeof value !== 'string') return null;
      if (!PASSWORD_LETTER_REGEX.test(value)) return t('common:validation.password.needsLetter');
      if (!PASSWORD_DIGIT_REGEX.test(value)) return t('common:validation.password.needsDigit');
      return null;
    }
    default:
      return null;
  }
}

/**
 * Maps a server error onto RHF fields. Returns true when handled (caller
 * must not toast). Handles CONFLICT (username taken; the only 409 source
 * on the two create-user mutations) and VALIDATION_FAILED details.
 */
export function applyServerFieldErrors<T extends FieldValues>({
  err,
  setError,
  fields,
  t,
  getValue,
}: {
  err: ApiErrorInfo;
  setError: UseFormSetError<T>;
  fields: readonly Path<T>[];
  t: TFunction;
  getValue?: (field: string) => unknown;
}): boolean {
  if (err.code === 'CONFLICT') {
    const usernameField = fields.find((f) => f === 'username');
    if (!usernameField) return false;
    setError(
      usernameField,
      { type: 'conflict', message: t('common:validation.username.taken') },
      { shouldFocus: true },
    );
    return true;
  }
  if (err.code === 'VALIDATION_FAILED') {
    let mapped = false;
    for (const detail of err.details ?? []) {
      const root = detail.path.split('.')[0];
      const field = fields.find((f) => f === root);
      if (!field) continue;
      setError(field, {
        type: `server.${detail.code}`,
        message: localizeServerDetail(detail, t, getValue) ?? detail.message,
      });
      mapped = true;
    }
    return mapped;
  }
  return false;
}
